import * as React from 'react';
import { WithSearch } from "@elastic/react-search-ui";
import { buildData, Config } from "../../helpers";
import { LinesPreloader } from "../Preloader";
import LineChartWidget from "../LineChartWidget";

const NO_RESULTS_DELAY_MS = 400;

function Chart({ results, wasSearched, isLoading, chartConfig, lineStyle }) {
  const { data: chartData, groups: chartGroups } = React.useMemo(() => {
    if (!wasSearched || results.length === 0) return { data: [], groups: [] };
    const normalized = results.map(entry => {
      const row = {};
      Object.entries(entry).forEach(([key, val]) => {
        if (val && typeof val === 'object' && 'raw' in val) {
          row[key] = val.raw;
        }
      });
      return row;
    });
    return buildData(normalized, chartConfig);
  }, [results, wasSearched, chartConfig]);

  // Delay committing to "no results" — a cascade correction (e.g., replacing a
  // count-0 sequence length after a model change) briefly lands a zero-hit
  // response before the follow-up search starts; without this delay that gap
  // flashes the empty message.
  const [emptyCommitted, setEmptyCommitted] = React.useState(false);
  React.useEffect(() => {
    if (isLoading || results.length > 0) {
      setEmptyCommitted(false);
      return;
    }
    const handle = setTimeout(() => setEmptyCommitted(true), NO_RESULTS_DELAY_MS);
    return () => clearTimeout(handle);
  }, [isLoading, results]);

  if (!wasSearched) return null;
  if (isLoading || (results.length === 0 && !emptyCommitted)) return <LinesPreloader/>;
  if (results.length === 0) return <p className='h--smaller mb-4'>{Config.I18n.Hits.NoResults}</p>;
  if (chartData.length === 0 || chartGroups.length === 0) return <LinesPreloader/>;

  return <LineChartWidget chartData={chartData} chartGroups={chartGroups} chartConfig={chartConfig} lineType={lineStyle}/>;
}

export default function HitsWidget({ chartConfig, lineStyle }) {
  return (
    <WithSearch mapContextToProps={({ results, wasSearched, isLoading }) => ({ results, wasSearched, isLoading })}>
      {({ results, wasSearched, isLoading }) => (
        <Chart results={results} wasSearched={wasSearched} isLoading={isLoading} chartConfig={chartConfig} lineStyle={lineStyle}/>
      )}
    </WithSearch>
  );
}
